import React, { useState, useEffect, useContext } from 'react';
import { 
  Container, Row, Col, Card, Button, Badge, 
  Alert, Spinner, Tabs, Tab, ProgressBar, Modal,
  ListGroup, ListGroupItem, Form
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBullseye, faCalendarCheck, faUserFriends, 
  faChartLine, faPlus, faBell, faCheck, faTimes,
  faInfoCircle, faClock, faExclamationTriangle, faPaperPlane,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { MyContext } from '../MainContext'; 
import { backendUrl } from '../utils/variables';
import GoalCreationForm from '../components/create_goal';
import GoalReportForm from '../components/goal_report';
import InvitePartnerModal from '../components/invite_partner';


import Header from '../components/header';
import Footer from '../components/footer';

// Helper function to safely parse dates for Safari compatibility
const parseDate = (dateString) => {
  if (!dateString) return null;
  
  // Replace space with 'T' for ISO format compatibility
  const isoString = dateString.replace(' ', 'T');
  
  try {
    return new Date(isoString);
  } catch (error) {
    console.error('Error parsing date:', dateString, error);
    return null;
  }
};

const missedCountFor = (goal) => parseInt(goal?.missed_reports, 10) || 0;

const missedLabel = (goal) => {
  const count = missedCountFor(goal);
  if (count <= 0) return null;
  return count === 1 ? '1 report missed' : `${count} reports missed`;
};

const formatSlotDate = (value) => {
  if (!value) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(value).slice(0, 10);
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date || !months[month - 1]) return day;
  return `${months[month - 1]} ${date}, ${year}`;
};

const isCurrentWindowOpen = (goal) => Boolean(goal?.is_report_due || goal?.is_overdue);

const GoalsDashboard = () => {
  const { user } = useContext(MyContext);
  const [goals, setGoals] = useState([]);
  const [partnerGoals, setPartnerGoals] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showGoalDetails, setShowGoalDetails] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedGoalDetails, setSelectedGoalDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('my-goals');
  const [showPartnersModal, setShowPartnersModal] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [selectedGoalForModal, setSelectedGoalForModal] = useState(null);
  const [showCloseGoalModal, setShowCloseGoalModal] = useState(false);
  const [goalToClose, setGoalToClose] = useState(null);
  const [showDeleteGoalModal, setShowDeleteGoalModal] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);

  const [showInvitePartnerModal, setShowInvitePartnerModal] = useState(false);
  
  // Encouragement states
  const [showEncouragementModal, setShowEncouragementModal] = useState(false);
  const [showEncouragementsModal, setShowEncouragementsModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [encouragementText, setEncouragementText] = useState('');
  const [encouragements, setEncouragements] = useState([]);
  const [sendingEncouragement, setSendingEncouragement] = useState(false);
  const [loadingEncouragements, setLoadingEncouragements] = useState(false);

  const [showRemovePartnerConfirm, setShowRemovePartnerConfirm] = useState(false);
  const [partnerToRemove, setPartnerToRemove] = useState(null);

  useEffect(() => {
    if (user) {
      loadGoals();
    }
  }, [user]);

  const loadGoals = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load user's goals
      const goalsResponse = await fetch(`${backendUrl}/list_goals.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id })
      });
      
      const goalsData = await goalsResponse.json();
      if (goalsData.success) {
        setGoals(goalsData.goals || []);
      } else {
        setError(goalsData.message || 'Failed to load goals');
      }
      
      // Load goals where user is a partner
      const partnerResponse = await fetch(`${backendUrl}/list_partner_goals.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id })
      });
      
      const partnerData = await partnerResponse.json();
      if (partnerData.success) {
        setPartnerGoals(partnerData.goals || []);
        setPendingRequests(partnerData.pending_requests || []);
      } else {
        console.error(partnerData.message || 'Failed to load partner goals');
        setPartnerGoals([]);
        setPendingRequests([]);
      }
    } catch (err) {
      setError('Network error: ' + err.message);
      setPartnerGoals([]);
      setPendingRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (partnershipId) => {

    console.log('Accepting partnership request:', partnershipId);

    try {
      const response = await fetch(`${backendUrl}/accept_partner_request.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ partnership_id: partnershipId })
      });
      
      const data = await response.json();
      if (data.success) {
        loadGoals();
      } else {
        setError(data.message || 'Failed to accept request');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }
  };

  const handleRejectRequest = async (partnershipId) => {
    try {
      const response = await fetch(`${backendUrl}/reject_partner_request.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ partnership_id: partnershipId })
      });
      
      const data = await response.json();
      if (data.success) {
        loadGoals();
      } else {
        setError(data.message || 'Failed to reject request');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }
  };

  const handleCloseGoal = async (goal) => {
    setGoalToClose(goal);
    setShowCloseGoalModal(true);
  };

  const confirmCloseGoal = async () => {
    if (!goalToClose) return;
    
    try {
      const response = await fetch(`${backendUrl}/close_goal.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          goal_id: goalToClose.id,
          user_id: user.user_id
        })
      });
      
      const data = await response.json();
      //console.log('Close goal response:', data);
      if (data.success) {
        loadGoals();
        setShowCloseGoalModal(false);
        setGoalToClose(null);
      } else {
        setError(data.messages || 'Failed to close goal');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }
  };

  const handleDeleteGoal = async (goal) => {
    setGoalToDelete(goal);
    setShowDeleteGoalModal(true);
  };

  const confirmDeleteGoal = async () => {
    if (!goalToDelete) return;
    
    try {
      const response = await fetch(`${backendUrl}/delete_goal.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          goal_id: goalToDelete.id,
          user_id: user.user_id
        })
      });
      
      const data = await response.json();
      if (data.success) {
        loadGoals();
        setShowDeleteGoalModal(false);
        setGoalToDelete(null);
      } else {
        setError(data.messages || 'Failed to delete goal');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }
  };

  // Encouragement functions
  const handleSendEncouragement = async () => {
    if (!encouragementText.trim()) return;
    
    // Determine the partner_id (sender)
    const partnerId = selectedPartner ? selectedPartner.id : user.user_id;
    
    try {
      setSendingEncouragement(true);
      setError(null);
      
      const response = await fetch(`${backendUrl}/send_encouragement.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          goal_id: selectedGoalForModal.id,
          partner_id: partnerId,
          encouragement_text: encouragementText.trim()
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setShowEncouragementModal(false);
        setEncouragementText('');
        setSelectedPartner(null);
        setError(null);
      } else {
        setError(data.message || 'Failed to send encouragement');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSendingEncouragement(false);
    }
  };

  const handleViewEncouragements = async (goal) => {
    try {
      setLoadingEncouragements(true);
      setError(null);
      
      const response = await fetch(`${backendUrl}/get_encouragements.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          goal_id: goal.id,
          user_id: user.user_id
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setEncouragements(data.encouragements);
        setShowEncouragementsModal(true);
      } else {
        setError(data.message || 'Failed to load encouragements');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoadingEncouragements(false);
    }
  };

  const handleRemovePartnerClick = (partnerId, partnerName) => {
    setPartnerToRemove({ id: partnerId, name: partnerName });
    setShowRemovePartnerConfirm(true);
  };
  
  const confirmRemovePartner = async () => {
    if (!partnerToRemove || !selectedGoalForModal) return;
    
    try {
      const response = await fetch(`${backendUrl}/remove_partner.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          goal_id: selectedGoalForModal.id,
          partner_id: partnerToRemove.id,
          user_id: user.user_id
        })
      });
      
      const data = await response.json();
      if (data.success) {
        loadGoals(); // Refresh goals to update partners list
        setError(null);
      } else {
        setError(data.message || 'Failed to remove partner');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setShowRemovePartnerConfirm(false);
      setPartnerToRemove(null);
    }
  };
  

  const getStatusBadge = (goal) => {
    if (!goal.is_active) return <Badge bg="secondary">Inactive</Badge>;
    const endDate = parseDate(goal.end_date);
    if (endDate && endDate < new Date()) {
      return <Badge bg="info">Completed</Badge>;
    }
    return <Badge bg="success">Active</Badge>;
  };

  const isGoalCompleted = (goal) => {
    const endDate = parseDate(goal.end_date);
    return endDate && endDate < new Date();
  };

  const getFrequencyText = (goal) => {
    if (goal.frequency_type === 'daily') return `Daily at ${goal.report_time}`;
    if (goal.frequency_type === 'weekly') {
      const days = goal.frequency_value.split(',').map(d => 
        ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][parseInt(d)]
      ).join(', ');
      return `Weekly on ${days} at ${goal.report_time}`;
    }
    if (goal.frequency_type === 'monthly') {
      return `Monthly on the ${goal.frequency_value}${['th','st','nd','rd'][((parseInt(goal.frequency_value)%100)-20)%10]||'th'} at ${goal.report_time}`;
    }
    return 'Custom schedule';
  };

  const handleReportClick = (goal, reportDate = null) => {
    setSelectedGoal({
      ...goal,
      report_target_date: reportDate || goal.overdue_date || null
    });
    setShowReportModal(true);
  };

  const handleViewDetails = (goal) => {
    setSelectedGoalDetails(goal);
    setShowGoalDetails(true);
  };

  const handleViewPartners = (goal) => {
    setSelectedGoalForModal(goal);
    setShowPartnersModal(true);
  };

  const handleViewReports = (goal) => {
    setSelectedGoalForModal(goal);
    setShowReportsModal(true);
  };

  const getReportStatus = (goal) => {
    const missed = missedLabel(goal);
    if (isCurrentWindowOpen(goal)) {
      return (
        <div>
          <div className="d-flex align-items-center text-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
            <span>
              Overdue
              {goal.overdue_date ? ` (${formatSlotDate(goal.overdue_date)})` : ''}
            </span>
          </div>
          {missed && (
            <Badge bg="warning" text="dark" className="mt-1">{missed}</Badge>
          )}
        </div>
      );
    } else if (goal.next_report_date) {
      const nextDate = parseDate(goal.next_report_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const nextDateOnly = new Date(nextDate);
      nextDateOnly.setHours(0, 0, 0, 0);
      
      // If next report date is today, show "Today" instead of the date
      if (nextDateOnly.getTime() === today.getTime()) {
        return (
          <div>
            <div className="d-flex align-items-center text-muted">
              <FontAwesomeIcon icon={faClock} className="me-1" />
              <span>Today at {goal.report_time}</span>
            </div>
            {missed && (
              <Badge bg="warning" text="dark" className="mt-1">{missed}</Badge>
            )}
          </div>
        );
      }
      
      return (
        <div>
          <div className="d-flex align-items-center text-muted">
            <FontAwesomeIcon icon={faClock} className="me-1" />
            <span>Next: {nextDate ? nextDate.toLocaleDateString() : 'Invalid date'}</span>
          </div>
          {missed && (
            <Badge bg="warning" text="dark" className="mt-1">{missed}</Badge>
          )}
        </div>
      );
    }
    return missed ? <Badge bg="warning" text="dark">{missed}</Badge> : null;
  };

  return (
    <>
      <Header />
      <Container fluid className="py-4" style={{ marginTop:'60px', marginBottom: '100px' }}>
        <Row className="mb-4">
          <Col>
            <h2>
              <FontAwesomeIcon icon={faBullseye} className="me-2 text-primary" />
              Goals & Accountability
            </h2>
            <p className="text-muted">Track your progress with accountability partners</p>
          </Col>
          <Col className="text-end">
            <Button 
              variant="primary" 
              onClick={() => setShowCreateModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Create New Goal
            </Button>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">

             <Tab eventKey="my-goals" title="My Goals">
            {loading ? (
                <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading your goals...</p>
                </div>
            ) : goals.length === 0 ? (
                <Card>
                <Card.Body className="text-center py-5">
                    <FontAwesomeIcon icon={faBullseye} size="3x" className="text-muted mb-3" />
                    <h4>No Goals Yet</h4>
                    <p className="text-muted">Create your first goal to get started</p>
                    <Button 
                    variant="primary" 
                    onClick={() => setShowCreateModal(true)}
                    >
                    Create Goal
                    </Button>
                </Card.Body>
                </Card>
            ) : (
                <Row>
                {goals.map(goal => (
                    <Col md={6} lg={4} key={goal.id} className="mb-4">
                    <Card className="h-100">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-1">
                                <strong>{goal.report_time}</strong>
                                <span className="text-muted ms-2">•</span>
                                <span className="text-muted ms-2">
                                    {goal.frequency_type === 'daily' ? 'Daily' :
                                     goal.frequency_type === 'weekly' ? 'Weekly' :
                                     goal.frequency_type === 'monthly' ? 'Monthly' : 'Custom'}
                                </span>
                            </div>
                            {goal.frequency_type === 'weekly' && goal.frequency_value && (
                                <small className="text-muted">
                                    Days: {goal.frequency_value.split(',').map(d =>
                                        ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][parseInt(d)]
                                    ).join(', ')}
                                </small>
                            )}
                        </div>
                        <div className="d-flex align-items-center">
                            {isCurrentWindowOpen(goal) && !isGoalCompleted(goal) && (
                                <Badge bg="danger" className="me-2">
                                    Overdue
                                </Badge>
                            )}
                            {missedLabel(goal) && !isGoalCompleted(goal) && (
                                <Badge bg="warning" text="dark" className="me-2">
                                    {missedLabel(goal)}
                                </Badge>
                            )}
                            {getStatusBadge(goal)}
                           
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        className="ms-2"
                                        onClick={() => handleCloseGoal(goal)}
                                        title="Close Goal"
                                        style={{ fontSize: '12px', padding: '2px 6px' }}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </Button>
                                    <Button 
                                        variant="outline-danger" 
                                        size="sm" 
                                        className="ms-2"
                                        onClick={() => handleDeleteGoal(goal)}
                                        title="Delete Goal"
                                        style={{ fontSize: '12px', padding: '2px 6px' }}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                             
     
                        </div>
                        </Card.Header>
                        <Card.Body>
                        <div className="mb-3">
                            <div className="d-flex align-items-start justify-content-between">
                                <div className="flex-grow-1 me-2">
                                    <h6 className="mb-1">{goal.title}</h6>
                                </div>
                                <Button 
                                    variant="link" 
                                    size="sm" 
                                    className="p-0 text-muted"
                                    onClick={() => {
                                        const descriptionElement = document.getElementById(`description-${goal.id}`);
                                        if (descriptionElement) {
                                            descriptionElement.style.display = 
                                                descriptionElement.style.display === 'none' ? 'block' : 'none';
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                </Button>
                            </div>
                            <div 
                                id={`description-${goal.id}`}
                                className="text-muted small mt-2"
                                style={{ display: 'none' }}
                            >
                                {goal.description || 'No description provided'}
                            </div>
                        </div>

                        {3<4 && <div className="mb-3">
                            <small className="">Report Status:</small>
                            <div className="mt-1">
                                {!isGoalCompleted(goal) ? getReportStatus(goal) : (
                                    <div className="text-muted">
                                        <FontAwesomeIcon icon={faCheck} className="me-1" />
                                        Goal completed
                                    </div>
                                )}
                            </div>
                        </div>}

                        <div className="mb-3">
                            <small className="text-muted">Partners:</small>
                            <div className="mt-1 d-flex align-items-center">
                                {goal.partners && goal.partners.length > 0 ? (
                                    <>
                                        <span className="me-2">{goal.partners.length} partner(s)</span>
                                        <Button 
                                            variant="link" 
                                            size="sm" 
                                            className="p-0 text-primary"
                                            onClick={() => handleViewPartners(goal)}
                                        >
                                            <FontAwesomeIcon icon={faUserFriends} />
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <span className="text-muted me-2">No partners yet</span>
                                        <Button 
                                            variant="link" 
                                            size="sm" 
                                            className="p-0 text-primary"
                                            onClick={() => {
                                                setSelectedGoal(goal);
                                                setShowInvitePartnerModal(true);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faUserFriends} />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex">
                                {!isGoalCompleted(goal) && isCurrentWindowOpen(goal) ? (
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm"
                                        onClick={() => handleReportClick(goal)}
                                        className="me-2"
                                    >
                                        Submit Report
                                    </Button>
                                ) : null}
                            </div>
                            <div>
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    onClick={() => handleViewReports(goal)}
                                    className="me-1"
                                >
                                    <FontAwesomeIcon icon={faChartLine} className="me-1" /> Reports
                                </Button>
                                {/*<Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    onClick={() => handleViewDetails(goal)}
                                >
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-1" /> Details
                                </Button>*/}
                            </div>
                        </div>
                        </Card.Body>
                    </Card>
                    </Col>
                ))}
                </Row>
            )}
     </Tab>

      <Tab eventKey="partner-requests" title={
            <span>
              Pending Requests
              {pendingRequests.length > 0 && (
                <Badge bg="warning" pill className="ms-2">
                  {pendingRequests.length}
                </Badge>
              )}
            </span>
          }>
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading requests...</p>
              </div>
            ) : pendingRequests.length === 0 ? (
              <Card>
                <Card.Body className="text-center py-5">
                  <FontAwesomeIcon icon={faBell} size="3x" className="text-muted mb-3" />
                  <h4>No Pending Requests</h4>
                  <p className="text-muted">You don't have any pending partner requests</p>
                </Card.Body>
              </Card>
            ) : (
              <Row>
                {pendingRequests.map(request => (
                  <Col md={6} lg={4} key={request.partnership_id} className="mb-4">
                    <Card>
                      <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="text-truncate" style={{ maxWidth: '180px' }} title={request.goal_title}>
                          {request.goal_title}
                        </div>
                        <Badge bg="warning">New</Badge>
                      </Card.Header>
                      <Card.Body>
                        <div className="mb-2">
                          <small className="text-muted d-block">
                            From: {request.owner_first_name} {request.owner_last_name}
                          </small>
                          <small className="text-muted d-block">
                            Username: @{request.owner_username}
                          </small>
                          <small className="text-muted d-block">
                            Requested: {parseDate(request.request_date)?.toLocaleDateString() || 'Invalid date'}
                          </small>
                        </div>
                      </Card.Body>
                      <Card.Footer className="d-flex justify-content-between p-2">
                        <Button 
                          variant="success" 
                          size="sm"
                          onClick={() => handleAcceptRequest(request.partnership_id)}
                          className="flex-grow-1 me-1"
                        >
                          <FontAwesomeIcon icon={faCheck} className="me-1" /> Accept
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleRejectRequest(request.partnership_id)}
                          className="flex-grow-1 ms-1"
                        >
                          <FontAwesomeIcon icon={faTimes} /> Reject
                        </Button>
                      </Card.Footer>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Tab>
          
          <Tab eventKey="partner-goals" title={
            <span>
              Partner Goals
              {partnerGoals.some(g => g.is_overdue) && (
                <Badge bg="danger" pill className="ms-2">
                  {partnerGoals.filter(g => g.is_overdue).length}
                </Badge>
              )}
            </span>
          }>
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading partner goals...</p>
              </div>
            ) : partnerGoals.length === 0 ? (
              <Card>
                <Card.Body className="text-center py-5">
                  <FontAwesomeIcon icon={faUserFriends} size="3x" className="text-muted mb-3" />
                  <h4>No Partner Goals</h4>
                  <p className="text-muted">You haven't been added as an accountability partner yet</p>
                </Card.Body>
              </Card>
            ) : (
              <Row>
                {partnerGoals.map(goal => (
                  <Col md={6} lg={4} key={goal.id} className="mb-4">
                    <Card className="h-100">
                      <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            <strong>{goal.report_time}</strong>
                            <span className="text-muted ms-2">•</span>
                            <span className="text-muted ms-2">
                              {goal.frequency_type === 'daily' ? 'Daily' : 
                               goal.frequency_type === 'weekly' ? 'Weekly' : 
                               goal.frequency_type === 'monthly' ? 'Monthly' : 'Custom'}
                            </span>
                          </div>
                          {goal.frequency_type === 'weekly' && goal.frequency_value && (
                            <small className="text-muted">
                              Days: {goal.frequency_value.split(',').map(d => 
                                ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][parseInt(d)]
                              ).join(', ')}
                            </small>
                          )}
                        </div>
                        <div className="d-flex align-items-center">
                          {goal.is_overdue && (
                            <Badge bg="danger" className="me-2">
                              Overdue
                            </Badge>
                          )}
                          {missedLabel(goal) && (
                            <Badge bg="warning" text="dark" className="me-2">
                              {missedLabel(goal)}
                            </Badge>
                          )}
                          <Badge bg={goal.goal_status === 'active' ? 'success' : 'secondary'}>
                            {goal.goal_status}
                          </Badge>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <div className="mb-3">
                          <div className="d-flex align-items-start justify-content-between">
                            <div className="flex-grow-1 me-2">
                              <h6 className="mb-1">{goal.title}</h6>
                            
                            </div>
                            <Button 
                              variant="link" 
                              size="sm" 
                              className="p-0 text-muted"
                              onClick={() => {
                                const descriptionElement = document.getElementById(`description-${goal.id}`);
                                if (descriptionElement) {
                                  descriptionElement.style.display = 
                                    descriptionElement.style.display === 'none' ? 'block' : 'none';
                                }
                              }}
                            >
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </Button>
                          </div>
                          <div 
                            id={`description-${goal.id}`}
                            className="text-muted small mt-2"
                            style={{ display: 'none' }}
                          >
                            {goal.description || 'No description provided'}
                          </div>
                        </div>

                        <div className="mb-2">
                          <small className="text-muted d-block">
                            Owner: {goal.owner_first_name} {goal.owner_last_name} (@{goal.owner_username})
                          </small>
                          {/*if comptetion rate is < 40 sshow in red, less than 60 show in yellow, more than 60 show in green*/}
                          {/* {parseInt(goal.total_reports)+parseInt(goal.reports_behind) where shows nan show 0 in such cases*/}
                          {/* instead of the low, medium high, let Progres:... statement be of the respective color and % based on the completion rate, the entire statement to be of the same color as the % */}

                          <small className={` d-block ${goal.completion_rate < 40 ? 'text-danger' : goal.completion_rate < 60 ? 'text-warning' : 'text-success'}`}>
                            Progress: <span>{goal.completion_rate}%</span> ({goal.completed_reports}/{parseInt(goal.total_reports)+parseInt(goal.reports_behind) || 0 })
                          </small>
                        </div>
                        
                        <div className="mb-3">
                          <small className="text-muted">Report Status:</small>
                          <div className="mt-1">
                            {isCurrentWindowOpen(goal) ? (
                              <div>
                                <div className="d-flex align-items-center text-danger">
                                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                  <span>
                                    Overdue
                                    {goal.overdue_date ? ` (${formatSlotDate(goal.overdue_date)})` : ''}
                                  </span>
                                </div>
                                {missedLabel(goal) && (
                                  <Badge bg="warning" text="dark" className="mt-1">{missedLabel(goal)}</Badge>
                                )}
                              </div>
                            ) : goal.next_report_date ? (
                              <div>
                                <div className="d-flex align-items-center text-muted">
                                  <FontAwesomeIcon icon={faClock} className="me-1" />
                                  <span>Next: {parseDate(goal.next_report_date)?.toLocaleDateString() || 'Invalid date'} at {goal.report_time}</span>
                                </div>
                                {missedLabel(goal) && (
                                  <Badge bg="warning" text="dark" className="mt-1">{missedLabel(goal)}</Badge>
                                )}
                              </div>
                            ) : (
                              <div>
                                <div className="text-muted">No reports scheduled</div>
                                {missedLabel(goal) && (
                                  <Badge bg="warning" text="dark" className="mt-1">{missedLabel(goal)}</Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="d-flex justify-content-end mt-3">
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => handleViewReports(goal)}
                            className="me-2"
                          >
                            <FontAwesomeIcon icon={faChartLine} className="me-1" /> View Reports
                          </Button>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => {
                              setSelectedGoalForModal(goal);
                              setShowEncouragementModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Encourage
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Tab>
        </Tabs>

        {selectedGoal && (
          <InvitePartnerModal 
            show={showInvitePartnerModal}
            onClose={() => {
              setShowInvitePartnerModal(false);
              loadGoals();
              setSelectedGoal(null);
            }}
            goal={selectedGoal}
            user={user}
          />
        )}

        <GoalCreationForm 
          show={showCreateModal} 
          onClose={() => setShowCreateModal(false)} 
          refreshGoals={loadGoals}
        />
        
        {selectedGoal && (
          <GoalReportForm 
            show={showReportModal}
            onClose={() => {
              setShowReportModal(false);
              setSelectedGoal(null);
            }}
            goal={selectedGoal}
            refreshGoals={loadGoals}
          />
        )}

        {/* Partners Modal */}
        <Modal show={showPartnersModal} onHide={() => setShowPartnersModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Partners for: {selectedGoalForModal?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedGoalForModal && selectedGoalForModal.partners && (
              <>
                <ListGroup>
                  {selectedGoalForModal.partners.map(partner => (
                    <ListGroupItem key={partner.id} className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{partner.first_name} {partner.last_name}</strong>
                        <div className="text-muted">@{partner.username}</div>
                      </div>
                      <div className="d-flex align-items-center">
                        <Badge 
                          bg={
                            partner.status === 'accepted' ? 'success' : 
                            partner.status === 'pending' ? 'warning' : 'secondary'
                          }
                          className="me-2"
                        >
                          {partner.status}
                        </Badge>
                        {(
                          <>
                          
                            <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => handleRemovePartnerClick(
                                      partner.id, 
                                      `${partner.first_name} ${partner.last_name}`
                                    )}
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </Button>

                          </>
                        )}
                      </div>
                    </ListGroupItem>
                  ))}
                </ListGroup>
                
                {selectedGoalForModal.partners.filter(p => p.status === 'accepted').length < 2 && (
                  <div className="mt-3 text-center">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => {
                        setSelectedGoal(selectedGoalForModal);
                        setShowPartnersModal(false);
                        setShowInvitePartnerModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faUserFriends} className="me-1" />
                      Invite More Partners
                    </Button>
                  </div>
                )}

                {/* View Encouragements Button */}
                <div className="mt-3 text-center">
                  <Button 
                    variant="outline-success" 
                    size="sm"
                    onClick={() => handleViewEncouragements(selectedGoalForModal)}
                    disabled={loadingEncouragements}
                  >
                    {loadingEncouragements ? (
                      <Spinner animation="border" size="sm" className="me-1" />
                    ) : (
                      <FontAwesomeIcon icon={faBell} className="me-1" />
                    )}
                    View Encouragements
                  </Button>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPartnersModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Reports Modal */}
        <Modal show={showReportsModal} onHide={() => setShowReportsModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Reports for: {selectedGoalForModal?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedGoalForModal && (
              <div>
                {/* Completed Reports Section */}
                {selectedGoalForModal.report_history && selectedGoalForModal.report_history.length > 0 && (
                  <div className="mb-4">
                    <h6>Completed Reports:</h6>
                    <ListGroup>
                      {[...selectedGoalForModal.report_history]
                        .sort((a, b) => String(b.report_date || '').localeCompare(String(a.report_date || '')))
                        .map((report, index) => (
                        <ListGroupItem key={index} className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-1">
                              <strong>{formatSlotDate(report.report_date) || 'Invalid date'}</strong>
                              <span className="text-muted ms-2">at {parseDate(report.submitted_at)?.toLocaleTimeString() || 'Invalid time'}</span>
                            </div>
                            {report.report_text && (
                              <div className="text-muted small">
                                "{report.report_text}"
                              </div>
                            )}
                          </div>
                          <Badge bg="success" size="sm" className="ms-2">
                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                            {report.status}
                          </Badge>
                        </ListGroupItem>
                      ))}
                    </ListGroup>
                  </div>
                )}

                {/* Current overdue window, then older missed dates */}
                {!isGoalCompleted(selectedGoalForModal) && (() => {
                  const isOwner = String(selectedGoalForModal.user_id || '') === String(user.user_id)
                    || String(selectedGoalForModal.owner_id || '') === String(user.user_id);
                  const missedDates = (Array.isArray(selectedGoalForModal.missed_dates)
                    ? selectedGoalForModal.missed_dates
                    : []
                  ).slice().sort((a, b) => String(a).localeCompare(String(b)));
                  const showOverdue = isCurrentWindowOpen(selectedGoalForModal) && selectedGoalForModal.overdue_date;

                  if (!showOverdue && missedDates.length === 0) return null;

                  return (
                    <>
                      {showOverdue && (
                        <div className="mb-4">
                          <h6>Overdue Report:</h6>
                          <ListGroup>
                            <ListGroupItem className="d-flex justify-content-between align-items-center bg-danger bg-opacity-10">
                              <div>
                                <strong>{formatSlotDate(selectedGoalForModal.overdue_date)}</strong>
                                <div className="text-muted">Current report window</div>
                              </div>
                              <div className="d-flex align-items-center">
                                <Badge bg="danger" className="me-2">
                                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                  Overdue
                                </Badge>
                                {isOwner && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                      handleReportClick(selectedGoalForModal, selectedGoalForModal.overdue_date);
                                      setShowReportsModal(false);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                                    Send Report
                                  </Button>
                                )}
                              </div>
                            </ListGroupItem>
                          </ListGroup>
                        </div>
                      )}
                      {missedDates.length > 0 && (
                        <div className="mb-4">
                          <h6>{missedLabel(selectedGoalForModal)}:</h6>
                          <ListGroup>
                            {missedDates.map((date) => (
                              <ListGroupItem
                                key={date}
                                className="d-flex justify-content-between align-items-center"
                              >
                                <div>
                                  <strong>{formatSlotDate(date)}</strong>
                                  <div className="text-muted">Missed report window</div>
                                </div>
                                <div className="d-flex align-items-center">
                                  <Badge bg="warning" text="dark" className="me-2">Missed</Badge>
                                  {isOwner && (
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => {
                                        handleReportClick(selectedGoalForModal, date);
                                        setShowReportsModal(false);
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                                      Fill Report
                                    </Button>
                                  )}
                                </div>
                              </ListGroupItem>
                            ))}
                          </ListGroup>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Next Due Date Info */}
                {!selectedGoalForModal.is_overdue && selectedGoalForModal.next_report_date && !isGoalCompleted(selectedGoalForModal) && (
                  <div className="mb-4">
                    <div className="alert alert-info">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faClock} className="me-2" />
                        <div>
                          <strong>Next Report Due:</strong> {parseDate(selectedGoalForModal.next_report_date)?.toLocaleDateString() || 'Invalid date'} at {selectedGoalForModal.report_time}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* No Reports Message */}
                {(!selectedGoalForModal.report_history || selectedGoalForModal.report_history.length === 0) &&
                 !isCurrentWindowOpen(selectedGoalForModal) &&
                 missedCountFor(selectedGoalForModal) === 0 &&
                 (!selectedGoalForModal.upcoming_reports || selectedGoalForModal.upcoming_reports.filter(report => {
                   const reportDate = parseDate(report.datetime);
                   const today = new Date();
                   today.setHours(23, 59, 59, 999);
                   return reportDate && reportDate <= today;
                 }).length === 0) && (
                  <div className="text-center text-muted py-3">
                    <FontAwesomeIcon icon={faChartLine} size="2x" className="mb-2" />
                    <p>No reports available yet</p>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowReportsModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showGoalDetails} onHide={() => setShowGoalDetails(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Goal Details: {selectedGoalDetails?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedGoalDetails && (
              <div>
                <p><strong>Description:</strong> {selectedGoalDetails.description || 'No description provided'}</p>
                <p><strong>Schedule:</strong> {getFrequencyText(selectedGoalDetails)}</p>
                <p><strong>Duration:</strong> {parseDate(selectedGoalDetails.start_date)?.toLocaleDateString() || 'Invalid date'} - 
                  {selectedGoalDetails.end_date ? parseDate(selectedGoalDetails.end_date)?.toLocaleDateString() || 'Invalid date' : ' Ongoing'}</p>
                <p><strong>Reports:</strong> {selectedGoalDetails.completed_reports || 0} completed of {selectedGoalDetails.total_reports || 0} total</p>
                
                {selectedGoalDetails.recent_reports?.length > 0 && (
                  <div className="mt-3">
                    <h6>Recent Reports:</h6>
                    <ul>
                      {selectedGoalDetails.recent_reports.map(report => (
                        <li key={report.id}>
                          {parseDate(report.report_date)?.toLocaleDateString() || 'Invalid date'}: 
                          <Badge bg={report.status === 'completed' ? 'success' : 'danger'} className="ms-2">
                            {report.status}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowGoalDetails(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showCloseGoalModal} onHide={() => setShowCloseGoalModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Goal Closure</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to close the goal "{goalToClose?.title}"? This action cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCloseGoalModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmCloseGoal}>
              Close Goal
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showDeleteGoalModal} onHide={() => setShowDeleteGoalModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Goal Deletion</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="alert alert-danger">
              <strong>Warning:</strong> Are you sure you want to delete the goal "{goalToDelete?.title}"? 
              This action will permanently remove the goal and all associated data including reports and partnerships. 
              This action cannot be undone.
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteGoalModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDeleteGoal}>
              Delete Goal
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Send Encouragement Modal */}
        <Modal show={showEncouragementModal} onHide={() => setShowEncouragementModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faPaperPlane} className="me-2 text-primary" />
              Send Encouragement
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedPartner && (
              <div className="mb-3">
                <p className="text-muted">
                  Sending encouragement to <strong>{selectedPartner.first_name} {selectedPartner.last_name}</strong>
                </p>
                <p className="text-muted small">
                  You can send one encouragement per day to each partner (max 500 characters).
                </p>
              </div>
            )}
            {selectedGoalForModal && !selectedPartner && (
              <div className="mb-3">
                <p className="text-muted">
                  Sending encouragement to <strong>{selectedGoalForModal.owner_first_name} {selectedGoalForModal.owner_last_name}</strong>
                </p>
                <p className="text-muted small">
                  You can send one encouragement per day to the goal owner (max 500 characters).
                </p>
              </div>
            )}
            <Form.Group>
              <Form.Label>Your Message of Encouragement</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={encouragementText}
                onChange={(e) => setEncouragementText(e.target.value)}
                placeholder="Share an encouraging message..."
                maxLength={500}
              />
              <small className="text-muted d-block text-end mt-1">
                {encouragementText.length}/500 characters
              </small>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEncouragementModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSendEncouragement}
              disabled={sendingEncouragement || encouragementText.trim() === ''}
            >
              {sendingEncouragement ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sending...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                  Send Encouragement
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* View Encouragements Modal */}
        <Modal show={showEncouragementsModal} onHide={() => setShowEncouragementsModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faBell} className="me-2 text-info" />
              Encouragements Received
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {encouragements.length === 0 ? (
              <div className="text-center py-4">
                <FontAwesomeIcon icon={faBell} size="3x" className="text-muted mb-3" />
                <h5>No Encouragements Yet</h5>
                <p className="text-muted">Your partners haven't sent you any encouragements yet.</p>
              </div>
            ) : (
              <ListGroup>
                {encouragements.map((encouragement, index) => (
                  <ListGroupItem key={index} className="mb-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <strong>{encouragement.first_name} {encouragement.last_name}</strong>
                        <div className="text-muted">@{encouragement.username}</div>
                      </div>
                      <small className="text-muted">
                        {parseDate(encouragement.created_at)?.toLocaleDateString() || 'Invalid date'}
                      </small>
                    </div>
                    <div className="bg-light p-3 rounded">
                      <p className="mb-0">{encouragement.encouragement_text}</p>
                    </div>
                  </ListGroupItem>
                ))}
              </ListGroup>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEncouragementsModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showRemovePartnerConfirm} onHide={() => setShowRemovePartnerConfirm(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Removal</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="alert alert-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              <strong>Are you sure you want to remove {partnerToRemove?.name}?</strong>
            </div>
            <p>
              This will remove {partnerToRemove?.name} as an accountability partner from 
              your goal "<strong>{selectedGoalForModal?.title}</strong>". 
              They will no longer be able to view your progress or send encouragements.
            </p>
          </Modal.Body>
          <Modal.Footer>
          <Button 
                variant="secondary" 
                onClick={() => {
                  setShowRemovePartnerConfirm(false);
                }}
              >
              Cancel
            </Button>

            <Button 
              variant="danger" 
              onClick={() => {
                confirmRemovePartner();
                setShowPartnersModal(false); 
              }}
            >
              Remove Partner
            </Button>
          </Modal.Footer>
        </Modal>

      </Container>
      <Footer page="goals" />
    </>
  );
};

export default GoalsDashboard;